import { useCallback, useState } from 'react';

import {
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_TYPE_ACTIVITY,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
} from 'global/constants';
import {
  ComponentAudio,
  ComponentColumns,
  ComponentDocument,
  ComponentEbookInteractive,
  ComponentGuidedResearch,
  ComponentImage,
  ComponentInfoBox,
  ComponentInteractive,
  ComponentLink,
  ComponentSlideshow,
  ComponentVideo,
  ComponentVocabulary,
  ComponentRiveAnimation,
  ComponentGif,
  ComponentImageSphere,
  ComponentDiscussion,
} from './component.constants';
import InfoBoxView from './info-box/info-box-view';
import DocumentView from './document/document-view';
import SlideshowView from './slideshow/slideshow-view';
import VideoView from './video/video-view';
import ImageView from './image/image-view';
import ImageSphereView from './image-sphere/image-sphere-view';
import InteractiveView from './interactive/interactive-view';
import InteractiveEbookView from './interactive-ebook/interactive-ebook-view';
import LinkView from './link/link-view';
import WebToolView from './webtool/webtool-view';
import GuidedResearchView from './guided-research/guided-research-view';
import ColumnsView from './columns/columns-view';
import RiveView from './rive/rive-view';
import GifView from './gif/gif-view';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityBuilderZoomComponentModal from '../activity-builder-zoom-component-modal';
import type { InteractiveLessonSectionFragment } from '../lesson-builder.graphql.generated';
import VocabularyView from './vocabulary/vocabulary-view';
import ComponentsViewWrapper from './components-view-wrapper';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
  ComponentEventData,
  ComponentExpandModeType,
  pushComponentViewEvent,
} from '../present/activity-builder-present-data-tracking.utils';
import type { InteractiveLessonSectionComponentFragment } from '../lesson-builder.graphql.generated';
import DiscussionView from './discussion/discussion-view';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  component: InteractiveLessonSectionComponentFragment;
  contentId: number;
  eventTrackingData?: ComponentEventData;
};

/**
 * Render structural components that can be interacted with such as
 * eBooks and rives.
 * @param component - The component to render
 * @param contentId - The content ID
 * @param eventTrackingData - The event tracking data
 * @returns
 */
const ComponentsLiveLayout = ({
  component,
  contentId,
  eventTrackingData,
}: Props) => {
  const [zoomedComponent, setZoomedComponent] = useState<
    Pluck<InteractiveLessonSectionFragment, 'components'> | undefined
  >();

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const handleOpenModal = useCallback(
    (
      component: Pluck<InteractiveLessonSectionFragment, 'components'>,
      expandMode?: ComponentExpandModeType
    ) => {
      setZoomedComponent(component);
      openModal();
      pushComponentViewEvent({
        component,
        eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
        expandMode,
        eventTrackingData,
        contentId,
      });
    },
    [contentId, eventTrackingData, openModal]
  );

  const openModalProps = {
    openModal: () => handleOpenModal(component),
  };

  return (
    <>
      {component.type !== ACTIVITIES_COMPONENT_TYPE_ACTIVITY ? (
        <ComponentsViewWrapper
          key={component.id}
          type={component.type}
          id={component.id}
        >
          {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX && (
            <InfoBoxView
              {...(component as ComponentInfoBox)}
              {...openModalProps}
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW && (
            <SlideshowView
              {...(component as ComponentSlideshow)}
              onComponentExpand={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT && (
            <DocumentView
              {...(component as ComponentDocument)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO && (
            <VideoView
              {...(component as ComponentVideo)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO && (
            <VideoView
              {...(component as ComponentAudio)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE && (
            <ImageView {...(component as ComponentImage)} {...openModalProps} />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE && (
            <ImageSphereView {...(component as ComponentImageSphere)} />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE && (
            <InteractiveView
              {...(component as ComponentInteractive)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE && (
            <InteractiveEbookView
              {...(component as ComponentEbookInteractive)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS && (
            <ColumnsView
              {...(component as ComponentColumns)}
              contentId={contentId}
              eventTrackingData={eventTrackingData}
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_LINK && (
            <LinkView
              {...(component as ComponentLink)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL && (
            <WebToolView
              {...(component as ComponentLink)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH && (
            <GuidedResearchView
              {...(component as ComponentGuidedResearch)}
              onStimulusOpen={() =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY && (
            <VocabularyView
              {...(component as ComponentVocabulary)}
              onComponentExpand={({ vocabularyWord, vocabularyWordCount }) =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                  eventTrackingData,
                  contentId,
                  vocabularyWord,
                  vocabularyWordCount,
                })
              }
            />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION && (
            <RiveView {...(component as ComponentRiveAnimation)} />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_GIF && (
            <GifView {...(component as ComponentGif)} {...openModalProps} />
          )}

          {component.type === ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE && (
            <DiscussionView
              {...(component as ComponentDiscussion)}
              onComponentExpand={expandMode =>
                pushComponentViewEvent({
                  component,
                  eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                  expandMode,
                  eventTrackingData,
                  contentId,
                })
              }
            />
          )}
        </ComponentsViewWrapper>
      ) : null}

      {isVisible && zoomedComponent && (
        <ActivityBuilderZoomComponentModal
          component={zoomedComponent}
          closeModal={() => {
            setZoomedComponent(undefined);
            closeModal();
          }}
        />
      )}
    </>
  );
};

export default ComponentsLiveLayout;
